import { type ReactNode } from 'react';
import { Eye, Filter } from 'lucide-react';
import {
  Button,
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
  ComboboxEmpty,
  ComboboxItem
} from '@bliks-io/ui';

interface FilterComboboxProps<TItem> {
  items: TItem[];
  selectedValues: string[];
  onValueChange: (value: string | string[] | undefined) => void;
  getItemValue: (item: TItem) => string;
  renderItem: (item: TItem) => ReactNode;
  triggerLabel: ReactNode;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  filterMode: 'highlight' | 'filter';
  onToggleFilterMode: () => void;
  highlightModeTitle: string;
  filterModeTitle: string;
  contentClassName?: string;
  search?: string;
  onSearchChange?: (s: string) => void;
}

export function FilterCombobox<TItem>({
  items,
  selectedValues,
  onValueChange,
  getItemValue,
  renderItem,
  triggerLabel,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  filterMode,
  onToggleFilterMode,
  highlightModeTitle,
  filterModeTitle,
  contentClassName,
  search,
  onSearchChange
}: FilterComboboxProps<TItem>) {
  if (items.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      <Combobox value={selectedValues} onValueChange={onValueChange} multiple>
        <ComboboxTrigger
          placeholder={placeholder}
          allowClear
          className="h-8 min-w-[160px] max-w-[220px] text-xs"
        >
          {triggerLabel}
        </ComboboxTrigger>
        <ComboboxContent align="end" className={contentClassName}>
          <ComboboxInput
            placeholder={searchPlaceholder}
            {...(search != null && onSearchChange
              ? { value: search, onValueChange: onSearchChange }
              : {})}
          />
          <ComboboxList>
            <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
            {items.map((item) => (
              <ComboboxItem key={getItemValue(item)} value={getItemValue(item)} className="gap-2">
                {renderItem(item)}
              </ComboboxItem>
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {selectedValues.length > 0 && (
        <Button
          variant="outline"
          size="small"
          className="w-8 h-8 p-0"
          onClick={onToggleFilterMode}
          title={filterMode === 'highlight' ? highlightModeTitle : filterModeTitle}
        >
          {filterMode === 'highlight' ? (
            <Eye className="w-3.5 h-3.5" />
          ) : (
            <Filter className="w-3.5 h-3.5" />
          )}
        </Button>
      )}
    </div>
  );
}
